import React, { useEffect, useState } from "react";

import {
  Autocomplete,
  Button,
  Checkbox,
  DocumentListItem,
  ExpandablePanel,
  ExpandablePanelBody,
  ExpandablePanelHeader,
  Modal,
  Select,
  SelectEvent,
  Table, TableMetadata,
  TextArea,
  TextField,
  Typography,
  useTable
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as UploadIcon } from "@king-ict/eupisi-ui/components/Icon/Upload.svg";
import { useValidation } from "@king-ict/eupisi-ui/hooks";
import { CodeBook, DocumentData } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { saveAs } from "file-saver";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  createProgramRequest,
  DocumentUploadRequest,
  downloadDocument,
  editProgramRequest,
  getActiveProgramTypes,
  getAndCheckInstitutionRequest,
  getProgramRequestDetail,
  uploadDocument
} from "../../api/nisooApi";
import { DocumentsUploadModal } from "../../components/DocumentsUploadModal";
import InstitutionBasicInfo from "../../components/InstitutionBasicInfo";
import OtherData from "../../components/OtherData";
import StaticField from "../../components/StaticField";
import UploadButton from "../../components/UploadButton";
import { useInput } from "../../hooks";
import { DocumentType } from "../../models/Document";
import { Institution, InstitutionLocation, LocationState } from "../../models/Institution";
import {
  ProgramType, ProgramTypeCodeBook,
} from "../../models/Program";
import {
  Competence,
  convertFromProgramRequest,
  // NISOO2 createOtherLegalEntityLocation,
  createProgramRequestData,
  mapProgramRequestData,
  OtherEnrollmentConditionId,
  OtherFormOfTeachingId,
  OtherKnowledgeCheckId,
  // NISOO2OtherLegalEntityLocation,
  ProgramRequestData,
  ProgramRequestDataValidationScheme,
  ProgramRequestDetail
} from "../../models/ProgramRequest";
import { AdditionalDocumentation } from "../../models/ProgramRequest/AdditionalDocumentation";
import { AdditionalDocumentationType } from "../../models/ProgramRequest/AdditionalDocumentationType";
import { createCompetenceInitial } from "../../models/ProgramRequest/Competence";
import { Module } from "../../models/ProgramRequest/Module";
import { ProgramRequestStatus } from "../../models/ProgramRequest/ProgramRequestStatus";
import {
  useGetEnrollmentConditionsQuery,
  useGetFormsOfTeachingQuery,
  useGetKnowledgeChecksQuery,
  useGetProgramsByTypeQuery
} from "../../queries/codeBookQuery";
import { useGetUserSessionQuery } from "../../queries/userQuery";
import { dashboardRoute } from "../../routes/routes";
import InstitutionPanel from "../Institution/InstitutionPanel";
import ProgramRequestModulePanel from "../ProgramRequest/ProgramRequestModulePanel";
import { CompetenceInfo } from "./CompetenceInfo";
import { ConditionalBasicInfo } from "./ConditionalBasicInfo";
import { OtherEnrollmentConditionsInfo } from "./OtherEnrollmentConditionsInfo";
import { OtherLegalEntityInfos } from "./OtherLegalEntityInfos";
import styles from "./ProgramRequestEdit.module.scss";
import TeacherInfo from "./TeacherInfo";

function syncOtherArray(
  ids: number[],
  otherValues: string[] | undefined,
  otherId: number
): string[] {
  if (!ids.includes(otherId)) {
    return [];
  }

  const currentValues = otherValues ?? [];

  if (!currentValues.length) {
    return [""];
  }

  return currentValues;
}

export function ProgramRequestEdit() {
  const [programRequest, setProgramRequest] = useState<ProgramRequestData>(createProgramRequestData());
  const [programRequestDetail, setProgramRequestDetail] = useState<ProgramRequestDetail>();
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openConfirmAndSendModal, setOpenConfirmAndSendModal] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [chackValidationError, setChackValidationError] = useState(false);
  const [openUploadAdditionalDocumentationModal, setOpenUploadAdditionalDocumentationModal] = useState(false);

  const { t } = useTranslation();
  const params = useParams<{ programRequestId: string }>();
  const history = useHistory();

  const [institutionData, setInstitutionData] = useState<Institution>();
  const [programTypes, setProgramTypes] = useState<ProgramTypeCodeBook[]>([]);
  const { data: userSession } = useGetUserSessionQuery();
  const { data: formsOfTeaching } = useGetFormsOfTeachingQuery();
  const { data: knowledgeChecks } = useGetKnowledgeChecksQuery();
  const { data: enrollmentConditions } = useGetEnrollmentConditionsQuery();
  const { data: programs } = useGetProgramsByTypeQuery(programRequest.programTypeId!, {
    skip: !programRequest.programTypeId,
  });

  const { handleChange } = useInput();

  const [errors, doValidations] = useValidation({
    data: programRequest,
    validationSchema: ProgramRequestDataValidationScheme,
  });

  const [isSendRequest, setIsSendRequest] = useState<boolean>(false);
  const [currentAdditionalDocumentationType, setCurrentAdditionalDocumentationType] = useState<AdditionalDocumentationType>();

  useEffect(() => {
    const getAndCheckInstitution = async () => {
      if (!userSession) {
        return;
      }

      var result = await getAndCheckInstitutionRequest(userSession.institutionId!);
      setInstitutionData(result);
    }
    getAndCheckInstitution();
  }, [userSession])

  useEffect(() => {
    const getProgramTypes = async (date?: Date) => {
      const response = await getActiveProgramTypes(date);
      setProgramTypes(response);
    }
    const getProgramRequestAsync = async () => {
      if (!params.programRequestId || isNaN(parseInt(params.programRequestId))) {
        await getProgramTypes();
        return;
      }
      const parsedProgramRequestId = parseInt(params.programRequestId);
      // const results = await getProgramRequest(parsedProgramRequestId);
      const results = await getProgramRequestDetail(parsedProgramRequestId);

      setProgramRequestDetail(results.programRequestDetail);

      // const programRequest = convertApiResponseToViewModel(results);
      const programRequest = mapProgramRequestData(results, results.programRequestDetail);
      //if (programRequestUnlocked.statusId !== ProgramRequestStatus.UnlockedASOO) {
      //  history.push(startRoute());
      //}
      setProgramRequest(programRequest);

      await getProgramTypes(programRequest.requestDate);
    }
    getProgramRequestAsync();



  }, [params.programRequestId, history])

  const fillLocations = () => {
    if (!institutionData || !institutionData.locations) {
      return codeBookToSelectData(undefined);
    } else {
      var locations: CodeBook[] = [];
      locations = makeCoodeBook(institutionData?.locations.filter(x => x.activityState == LocationState.Active || (x.deactivateDate && new Date(x.deactivateDate) > new Date())));
      return codeBookToSelectData(locations);
    }
  };

  function makeCoodeBook(institutionLocations: InstitutionLocation[]) {
    var locations: CodeBook[] = [];
    institutionLocations.forEach(institutionLocation => {
      var obj: CodeBook = {
        id: institutionLocation.id,
        name: institutionLocation.address
      };
      locations.push(obj);
    });

    // Return it
    return locations;
  }

  function makeIds(institutionLocations: InstitutionLocation[]) {
    var locations: number[] = [];
    institutionLocations.forEach(institutionLocation => {
      locations.push(institutionLocation.id);
    });

    // Return it
    return locations;
  }

  const programRequestCompetencesTableMetadata: TableMetadata<Competence> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "programRequestCompetence",
        header: t("request.competenceName"),
        accessor: (item: Competence) => {
          return (
            <div>
              <StaticField
                label={""}
                value={item.name}
                className={styles.changedCompetence}
              />
            </div>
          );
        }
      }
    ],
    rowToolTip: () => undefined
  }

  var [tableProps] = useTable<string>(
    {
      metadata: programRequestCompetencesTableMetadata,
      data: programRequest.competences,
      pagination: false,
      totalItems: programRequest.competences.length
    }
  );


  const getProgramsSelectData = () => {
    if (programs == null) {
      return codeBookToSelectData(undefined);
    } else if (
      programRequest.programTypeId &&
      (
        programRequest.programTypeId === ProgramType.TrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsSpecializationPrograms ||
        programRequest.programTypeId === ProgramType.RefinementPrograms         
      )
    ) {
      return codeBookToSelectData([
        { id: -1, name: t("common.nothingOffered") },
        ...programs,
      ]);
    } else {
      return codeBookToSelectData(programs);
    }
  };

  const handleTypeChange = (event: any) => {
    const programTypeId = event.target.value;
    let newModules = [] as Module[]
    if (programTypeId === ProgramType.TrainingPrograms ||
      programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
      programTypeId === ProgramType.CraftsSpecializationPrograms ||
      programTypeId === ProgramType.RefinementPrograms ||      
      programTypeId === ProgramType.NewVocationalCurriculum) {
      newModules.push({ name: "", learningSets: [{ name: "", code: "", csvet: 1, level: 2 }] });
    };

    let newCompetences = [] as Competence[];
    newCompetences.push(createCompetenceInitial());

    setProgramRequest((prev) => ({
      ...prev,
      programId: undefined,
      qualificationTypeId: undefined,
      languageLevelBusinessIds: [],
      languageLevelGeneralIds: [],
      educationSectorId: undefined,
      educationSubSectorId: undefined,
      professionId: undefined,
      programNameFreeEntry: undefined,
      programTypeId: event.target.value,
      hkoLevelId: 0,
      modules: newModules,
      competences: newCompetences
    }));
  };

  const handleProgramChange = (event: any) => {
    const programId = event.target.value;

    var program = programs?.find(pr => pr.id === programId);

    if (program) {
      var hkoLevelId = program.hkoLevelId;
      var qualificationTypeId = program.qualificationTypeId;
      var educationSectorId = program.educationSectorId;
      var educationSubSectorId = program.educationSubSectorId;

      setProgramRequest((prev) => ({
        ...prev,
        programId: programId,
        hkoLevelId: hkoLevelId,
        qualificationTypeId: qualificationTypeId,
        educationSectorId: educationSectorId,
        educationSubSectorId: educationSubSectorId
      }));
    }
    else {
      setProgramRequest((prev) => ({
        ...prev,
        programId: programId        
      }));
    }
  };

  const handleAdditionalDocumentationRemove = (index: number) => {    
    const newAdditionalDocumentation: AdditionalDocumentation[] = [...programRequest.additionalDocumentation];

    if (index !== -1) {
      newAdditionalDocumentation.splice(index, 1);
      setProgramRequest((prev) => ({
        ...prev,
        additionalDocumentation: newAdditionalDocumentation
      }));
    }
  };

  const handleAdditionalDocumentationDownload = (doc: DocumentData) => {
    if (doc.id === undefined) {
      return;
    }
    if (doc.id === -1) {
      saveAs(doc.file!, doc.fileName);
    }
    else {
      downloadDocument(DocumentType.AdditionalDocumentation, doc.id);
    }

  };
  
  const handleAddAdditionalDocumentationClick = (additionalDocumentationType: AdditionalDocumentationType) => {
    setCurrentAdditionalDocumentationType(additionalDocumentationType);
    setOpenUploadAdditionalDocumentationModal(true);
  };

  const handleCurriculumProgramAdded = async (file: File, event: React.ChangeEvent<HTMLInputElement>) => {
    var curriculumDocument: DocumentData = {
      id: -1,
      fileName: file.name,
      file: file
    };
    setProgramRequest((prev) => ({
      ...prev,
      curriculumProgramDocument: curriculumDocument
    }));
  };

  const handleCurriculumProgramRemoved = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (programRequest.curriculumProgramDocument === undefined || programRequest.curriculumProgramDocument.id === undefined) {
      return;
    }

    setProgramRequest((prev) => ({
      ...prev,
      curriculumProgramDocument: undefined
    }));
  };
  // NISOO 2
  //const handleOtherLegalEntityLocationsChanged = (event: ChangeEvent<HTMLInputElement>) => {
  //  const otherLegalEntityChecked = event.target.checked;

  //  let newOtherLegalEntities: OtherLegalEntityLocation[] = [];
  //  if (otherLegalEntityChecked) {
  //    newOtherLegalEntities = [createOtherLegalEntityLocation()];
  //  }

  //  setProgramRequest((prev) => ({
  //    ...prev,
  //    otherLegalEntityLocations: newOtherLegalEntities,
  //    locationOtherLegalEntity: otherLegalEntityChecked
  //  }));
  //};

  const handleLocationOutsideHeadquartersChanged = (event: React.ChangeEvent<SelectEvent>) => {
    var newLocationOutsideHeadquarters = event.target.value as number[];
    setProgramRequest((prev) => ({
      ...prev,
      locationOutsideHeadquartersIds: newLocationOutsideHeadquarters,
    }));

  }

  const handleEnrollmentConditionsChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newEnrollmentConditions = event.target.value as number[];

    const newOtherEnrollmentConditions = syncOtherArray(
      newEnrollmentConditions,
      programRequest.otherEnrollmentConditions,
      OtherEnrollmentConditionId
    );

    setProgramRequest((prev) => ({
      ...prev,
      enrollmentConditionsIds: newEnrollmentConditions,
      otherEnrollmentConditions: newOtherEnrollmentConditions,
    }));
  }

  const handleKnowledgeChecksChange = (event: React.ChangeEvent<SelectEvent>) => {
    const newKnowledgeChecks = event.target.value as number[];

    const newOtherKnowledgeChecks = syncOtherArray(
      newKnowledgeChecks,
      programRequest.otherKnowledgeChecks,
      OtherKnowledgeCheckId
    );

    setProgramRequest((prev) => ({
      ...prev,
      knowledgeChecksIds: newKnowledgeChecks,
      otherKnowledgeChecks: newOtherKnowledgeChecks,
    }));
  };

  const handleFormsOfTeachingChange = (event: React.ChangeEvent<SelectEvent>) => {
    var newFormsOfTeaching = event.target.value as number[];

    const newOtherForms = syncOtherArray(
      newFormsOfTeaching,
      programRequest.otherFormsOfTeaching,
      OtherFormOfTeachingId
    );
    setProgramRequest((prev) => ({
      ...prev,
      formOfTeachingIds: newFormsOfTeaching,
      otherFormsOfTeaching: newOtherForms
    }));
  };

  const getErrorForPath = (path: string): string | undefined => {
    path = path.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    path = path.replace(/^\./, '');           // strip a leading dot
    var pathKeys = path.split('.');
    var result = errors as any;

    for (let i = 0; i < pathKeys.length; ++i) {
      var key = pathKeys[i];
      if (result && typeof (result) === 'object' && result.hasOwnProperty(key)) {
        if (key.startsWith("competences")) {
          let programTypeId = programRequest.programTypeId;
          if (programTypeId === ProgramType.BasicEducationPrograms ||
            programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
            programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
            programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
            programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
            programTypeId === ProgramType.BasicArtsPrograms ||
            programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
            programTypeId === ProgramType.CroatianLanguageForForeigners) {
            return undefined;
          }
        }
        else if ((key.toLowerCase().startsWith("agencyexpertopiniondocuments") ||
          key.toLowerCase().startsWith("executionresolutiondocuments")) &&
          pathKeys.length === 2 && pathKeys[1] === "issueDate") {
          let programTypeId = programRequest.programTypeId;
          if (programTypeId === ProgramType.BasicEducationPrograms ||
            programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
            programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
            programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
            programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
            programTypeId === ProgramType.BasicArtsPrograms ||
            programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
            programTypeId === ProgramType.CroatianLanguageForForeigners) {
            return undefined;
          }
        }
        result = result[key] as any;
      } else {
        return undefined;
      }
    }
    return result as string;
  }

  const submitProgramRequest = async (sendRequest: boolean) => {
    setIsSendRequest(sendRequest);
    const isValid = doValidations();    
    if (!isValid && !chackCompetenceValidation()) {
      if (!errors) {
        // nasty workaround, did not found better solution to check competence and 
        // execution resolution date validaion
        setChackValidationError(true);
      }
      return;
    }
    if (sendRequest) {
      setOpenConfirmAndSendModal(true);
    }
    else {
      setOpenConfirmModal(true);
    }
  }
  // workaround for problem with yup validation, during validation progamTypeId in undwfined
  const chackCompetenceValidation = () => {
    var programTypeId = programRequest.programTypeId;

    if (errors && (programTypeId === ProgramType.BasicEducationPrograms ||
      programTypeId === ProgramType.MiddleSchoolShorterThanThreeYearsPrograms ||
      programTypeId === ProgramType.MiddleSchoolThreeYearsPrograms ||
      programTypeId === ProgramType.MiddleSchoolLongerThanThreeYearsPrograms ||
      programTypeId === ProgramType.ForeignLanguageLearningPrograms ||
      programTypeId === ProgramType.BasicArtsPrograms ||
      programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears ||
      programTypeId === ProgramType.CroatianLanguageForForeigners)) {
      var properties = Object.getOwnPropertyNames(errors);

      var competences = properties.filter(p => p.startsWith("competences"));

      return (properties.length === competences.length);
    }
    return false;
  }

  if (chackValidationError && errors) {
    setChackValidationError(false);
    if (chackCompetenceValidation()) {
      setOpenConfirmModal(true);
    }
  }
  const handleCancelConfirmModal = async () => {
    setOpenConfirmModal(false);
  }

  const handleCancelConfirmAndSendModal = async () => {
    setOpenConfirmAndSendModal(false);
  }


  const handleConfirmedSaveModal = async () => {
    setOpenConfirmModal(false);

    await uploadOtherLegalEntityLocationsDocuments();
    await uploadCurriculumProgramDocument();
    await uploadTeachersDocuments();
    await uploadAdditionalDocumentationDocuments();
        
    const request = convertFromProgramRequest(programRequest);
        
    if (programRequest.statusId === ProgramRequestStatus.Evaluated) {
      request.newStatus = ProgramRequestStatus.SentMZO;
    }

    // For only save, we don't do any status changes.
    if (params.programRequestId && !isNaN(parseInt(params.programRequestId))) {
      editProgramRequest(parseInt(params.programRequestId), request).then(() => {
        setOpenSuccessModal(true);
      });
    }
    else {
      createProgramRequest(request).then(() => {
        setOpenSuccessModal(true);
      });
    }
  }

  const uploadCurriculumProgramDocument = async () => {
    if (programRequest.curriculumProgramDocument && programRequest.curriculumProgramDocument.id === -1) {      
      const curriculumDocumentRequest = createDocumentRequest(programRequest.curriculumProgramDocument!.file!, DocumentType.CurriculumProgram);
      await uploadDocument(curriculumDocumentRequest).then((idDoc) => {
        var curriculumProgramDocument = programRequest.curriculumProgramDocument;
        curriculumProgramDocument!.id = idDoc;
        setProgramRequest((prev) => ({
          ...prev,
          curriculumProgramDocument: curriculumProgramDocument
        }));
      });
    }
  }

  const uploadOtherLegalEntityLocationsDocuments = async () => {
    if (programRequest.otherLegalEntityLocations && programRequest.otherLegalEntityLocations.length > 0) {
      programRequest.otherLegalEntityLocations.map(async (location, locationIndex) => {
        if (location.programExecutionRequestDocument && location.programExecutionRequestDocument.id === -1) {          
          const programExecutionRequestDocumentRequest = createDocumentRequest(location.programExecutionRequestDocument!.file!, DocumentType.OtherLegalEntityProgramExecutionRequest);
          await uploadDocument(programExecutionRequestDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.programExecutionRequestDocument!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }

        if (location.ministryConsentDocument && location.ministryConsentDocument.id === -1) {          
          const ministryConsentDocumentRequest = createDocumentRequest(location.ministryConsentDocument!.file!, DocumentType.OtherLegalEntityMinistryConsent);
          await uploadDocument(ministryConsentDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.ministryConsentDocument!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }

        if (location.personnelConditionsDocument && location.personnelConditionsDocument.id === -1) {          
          const personnelConditionsDocumentRequest = createDocumentRequest(location.personnelConditionsDocument!.file!, DocumentType.OtherLegalEntityPersonnelConditions);
          await uploadDocument(personnelConditionsDocumentRequest).then((idDoc) => {
            var otherLocationsList = programRequest.otherLegalEntityLocations!.slice();
            if (otherLocationsList && otherLocationsList.length > 0) {
              otherLocationsList[locationIndex]!.personnelConditionsDocument!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                otherLegalEntityLocations: otherLocationsList
              }));
            }
          });
        }
      });
    }
  }

  const uploadTeachersDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (programRequest.teachers && programRequest.teachers.length > 0) {
      programRequest.teachers!.map((teacher, teacherIndex) => {
        if (teacher.diplomaDocument && teacher.diplomaDocument.id === -1) {          
          const diplomaDocumentRequest = createDocumentRequest(teacher.diplomaDocument!.file!, DocumentType.TeacherDiploma);
          const diplomaDocumentPromise = uploadDocument(diplomaDocumentRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.diplomaDocument!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(diplomaDocumentPromise);
        }
        if (teacher.additionalDocumentation && teacher.additionalDocumentation.id === -1) {          
          const additionalDocumentationRequest = createDocumentRequest(teacher.additionalDocumentation!.file!, DocumentType.TeacherAdditional);
          const additionalDocumentationPromise = uploadDocument(additionalDocumentationRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.additionalDocumentation!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(additionalDocumentationPromise);
        }
        if (teacher.statement && teacher.statement.id === -1) {          
          const statementDocumentRequest = createDocumentRequest(teacher.statement!.file!, DocumentType.TeacherStatement);
          const statementDocumentPromise = uploadDocument(statementDocumentRequest).then((idDoc) => {
            var teachersList = programRequest.teachers!.slice();
            if (teachersList && teachersList.length > 0) {
              teachersList[teacherIndex]!.statement!.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                teachers: teachersList
              }));
            }
          });
          promises.push(statementDocumentPromise);
        }

        if (teacher.languages && teacher.languages.length > 0) {
          teacher.languages.map((language, languageIndex) => {
            if (language.certificationDocument && language.certificationDocument.id === -1) {
              const languageCertificationRequest = createDocumentRequest(language.certificationDocument!.file!, DocumentType.TeacherLanguageCertification);
              const languageCertificationPromise = uploadDocument(languageCertificationRequest).then((idDoc) => {
                var teachersList = programRequest.teachers!.slice();
                if (teachersList && teachersList.length > 0) {
                  teachersList[teacherIndex]!.languages![languageIndex]!.certificationDocument!.id = idDoc;
                  setProgramRequest((prev) => ({
                    ...prev,
                    teachers: teachersList
                  }));
                }
              });
              promises.push(languageCertificationPromise);
            }

          });
        }

      });
      await Promise.all(promises);
    }
  }


  const uploadAdditionalDocumentationDocuments = async () => {
    const promises = [] as Promise<void>[];

    if (programRequest.additionalDocumentation && programRequest.additionalDocumentation.length > 0) {
      programRequest.additionalDocumentation.map((document, documentIndex) => {
        if (document && document.additionalDocumentationDocument.id === -1) {          
          const additionalDocumentationDocumentRequest = createDocumentRequest(document.additionalDocumentationDocument.file!, DocumentType.AdditionalDocumentation);
          const additionalDocumentationDocumentPromise = uploadDocument(additionalDocumentationDocumentRequest).then((idDoc) => {
            var additionalDocumentationList = programRequest.additionalDocumentation.slice();
            if (additionalDocumentationList && additionalDocumentationList.length > 0) {
              additionalDocumentationList[documentIndex]!.additionalDocumentationDocument.id = idDoc;
              setProgramRequest((prev) => ({
                ...prev,
                additionalDocumentation: additionalDocumentationList
              }));
            }
          });
          promises.push(additionalDocumentationDocumentPromise)
        }

      });
      await Promise.all(promises);
    }
  };

  const handleConfirmedSaveAndSendModal = async () => {
    setOpenConfirmAndSendModal(false);

    await uploadOtherLegalEntityLocationsDocuments();
    await uploadCurriculumProgramDocument();
    await uploadTeachersDocuments();
    await uploadAdditionalDocumentationDocuments();

    const request = convertFromProgramRequest(programRequest);

    // For save and send, do status changes. It shouldn't matter 
    // what status is set. The backend should determine the correct 
    // status.
    if (!programRequest.statusId || programRequest.statusId === ProgramRequestStatus.Opened) {
      request.newStatus = ProgramRequestStatus.SubmittedASOO;
    }
    else if (programRequest.statusId === ProgramRequestStatus.Evaluated || programRequest.statusId === ProgramRequestStatus.SentMZO) {
      request.newStatus = ProgramRequestStatus.SubmittedMZO;
    } else if (programRequest.statusId === ProgramRequestStatus.UnlockedASOO) {
      request.newStatus = ProgramRequestStatus.SubmittedASOO;
    } else if (programRequest.statusId === ProgramRequestStatus.UnlockedMZO) {
      request.newStatus = ProgramRequestStatus.SubmittedMZO;
    }

    if (params.programRequestId && !isNaN(parseInt(params.programRequestId))) {
      editProgramRequest(parseInt(params.programRequestId), request).then(() => {
        setOpenSuccessModal(true);
      });
    }
    else {
      createProgramRequest(request).then(() => {
        setOpenSuccessModal(true);
      });
    }
  }
    
  const createDocumentRequest = (documentFile: File, documentTypeID: DocumentType): DocumentUploadRequest => {
    return {
      document: documentFile,
      documentTypeId: documentTypeID  
    };
  }

  const handleConfirmedSuccessModal = () => {
    setOpenSuccessModal(false)
    navigateToDashboard();
  }

  const cancelProgramRequest = async () => {
    setOpenCancelModal(true);
  }

  const handleCancelCancelModal = async () => {
    setOpenCancelModal(false);
  }

  const handleConfirmedCancelModal = async () => {
    setOpenCancelModal(false);
    navigateToDashboard();
  }

  const navigateToDashboard = () => {
    history.push(dashboardRoute());
  }

  const handleAdditionalDocumentationConfirm = (documents: DocumentData[]) => {
    if (documents && documents.length > 0) {
      const additionalDocumentationDocuments = programRequest.additionalDocumentation ? programRequest.additionalDocumentation.slice() : [];
      const additionalDocumentation : AdditionalDocumentation[] = documents.map(doc => ({        
        additionalDocumentationTypeId: currentAdditionalDocumentationType!,
        additionalDocumentationDocument: doc
      }));
      additionalDocumentationDocuments.push(...additionalDocumentation);      
      const newProgramRequest: ProgramRequestData = {
        ...programRequest,
        additionalDocumentation: additionalDocumentationDocuments
      };

      setProgramRequest(newProgramRequest);
    }

    setOpenUploadAdditionalDocumentationModal(false);

  };

  const canEditBasicCheck = () => {
    if (programRequest.statusId === ProgramRequestStatus.Evaluated || programRequest.statusId === ProgramRequestStatus.SentMZO
      || programRequest.statusId === ProgramRequestStatus.UnlockedMZO) {
      return false;
    }
    return true;
  };



  return (
    <>
      <Row>
        <Col md={4}>
          <InstitutionBasicInfo />
        </Col>
        <Col md={8} className={styles.title}>
          <Typography variant="largeBold" component="h2">
            {t("request.title")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={"pt-3"}>
          <ExpandablePanel clickableHeader className={styles.panel}>
            <ExpandablePanelHeader>
              {t("request.institutionDataPanelTitle")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody>
              {
                institutionData &&
                <InstitutionPanel institution={institutionData} />
              }
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={"pt-3"}>
          <ExpandablePanel expanded clickableHeader className={styles.panel}>
            <ExpandablePanelHeader>
              {t("request.programDataPanelTitle")}
            </ExpandablePanelHeader>
            <ExpandablePanelBody className={styles.programPanelBody}>
              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.programBasicInfo")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  {!canEditBasicCheck() &&
                    <StaticField
                      label={t("common.programType")}
                      value={programTypes.filter(pt => pt.id === programRequest.programTypeId)[0]?.name ?? ""}
                      className={styles.changedField}
                    />
                    ||
                    <Select
                      label={t("request.programType")}
                      name={nameof<typeof programRequest>("programTypeId")}
                      value={programRequest.programTypeId}
                      onChange={handleTypeChange}
                      errorText={errors?.programTypeId}
                      selectData={codeBookToSelectData(programTypes)}
                    />
                  }
                </Col>
                {(programRequest.programTypeId === ProgramType.BasicArtsPrograms
                  || programRequest.programTypeId === ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears || programRequest.programTypeId === ProgramType.CroatianLanguageForForeigners)
                  ? (
                    <Col md={4}>
                      <TextField
                        label={t("common.programDuration")}
                        name={nameof<typeof programRequest>("guidedLearningDuration")}
                        type="number"
                        value={programRequest.guidedLearningDuration}
                        onChange={handleChange(setProgramRequest)}
                        maxLength={5}
                        min={1}
                        errorText={getErrorForPath("durationChosen") ?? errors?.guidedLearningDuration}
                        readonly={!canEditBasicCheck()}
                        plaintext={!canEditBasicCheck()}
                      />
                    </Col>
                  ) : (
                    <Col md={4}>
                      <TextField
                        label={t("common.programDuration")}
                        type="number"
                        value={
                          (Number(programRequest.guidedLearningDuration) || 0) +
                          (Number(programRequest.independentLearningDuration) || 0) +
                          (Number(programRequest.workLearningDuration) || 0)
                        }
                        readonly
                        plaintext
                      />
                    </Col>
                  )
                }
                {programRequest.modules && programRequest.modules.length > 0 &&
                  <Col md={4}>
                    <TextField
                      label={t("request.CSVETTotal")}
                      type="number"
                      value={programRequest.modules.reduce((a, v) => a + v.learningSets.reduce((a, v) => a + (isNaN(v.csvet) ? 0 : v.csvet), 0), 0)}
                      readonly
                      plaintext
                    />
                  </Col>
                }
              </Row>
              {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
                && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
                <Row>
                  <Col md={4}>
                    <TextField
                      label={t("request.guidedLearningDuration")}
                      name={nameof<typeof programRequest>("guidedLearningDuration")}
                      type="number"
                      value={programRequest.guidedLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.guidedLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={!canEditBasicCheck()}
                      plaintext={!canEditBasicCheck()}
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("request.workLearningDuration")}
                      name={nameof<typeof programRequest>("workLearningDuration")}
                      type="number"
                      value={programRequest.workLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.workLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={!canEditBasicCheck()}
                      plaintext={!canEditBasicCheck()}
                    />
                  </Col>
                  <Col md={4}>
                    <TextField
                      label={t("request.independentLearningDuration")}
                      name={nameof<typeof programRequest>("independentLearningDuration")}
                      type="number"
                      value={programRequest.independentLearningDuration}
                      errorText={getErrorForPath("durationChosen") ?? errors?.independentLearningDuration}
                      onChange={handleChange(setProgramRequest)}
                      maxLength={5}
                      min={1}
                      readonly={!canEditBasicCheck()}
                      plaintext={!canEditBasicCheck()}
                    />
                  </Col>
                </Row>
              }
              <Row>
                <Col md={12}>
                  {!canEditBasicCheck()
                    &&
                    <>
                      <StaticField
                        label={t("common.programName")}
                        value={programs?.find(pr => pr.id === programRequest.programId)?.name ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Autocomplete
                      label={t("common.programName")}
                      name={nameof<typeof programRequest>("programId")}
                      value={programRequest.programId}
                      onChange={handleProgramChange}
                      selectData={getProgramsSelectData()}
                      errorText={errors?.programId}
                      infoText={t("request.programNameInfoMessage")}
                    />
                  }
                </Col>
              </Row>
              {programs && (
                <ConditionalBasicInfo
                  programTypeId={programRequest.programTypeId}
                  programRequestData={programRequest}
                  programRequestDetail={programRequestDetail}
                  programData={programs.find(pr => pr.id === programRequest.programId)}
                  onChange={handleChange(setProgramRequest)}
                  errors={errors}                  
                  readonly={!canEditBasicCheck()}
                />
              )}
              <Row className={styles.rowSpacing}>
                <Col md={4}>
                  {!canEditBasicCheck() &&
                    <>
                      <StaticField
                        label={t("request.formOfTeaching")}
                        value={formsOfTeaching?.filter(ft => programRequest.formOfTeachingIds.includes(ft.id)).map(ft => ft.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.formOfTeaching")}
                      name={nameof<typeof programRequest>("formOfTeachingIds")}
                      value={programRequest.formOfTeachingIds}
                      onChange={handleFormsOfTeachingChange}
                      selectData={codeBookToSelectData(formsOfTeaching)}
                      errorText={errors?.formOfTeachingIds?.toString()}
                    />

                  }
                </Col>
                <Col md={4}>
                  {!canEditBasicCheck() &&
                    <>
                      <StaticField
                        label={t("request.knowledgeChecks")}
                        value={knowledgeChecks?.filter(kc => programRequest.knowledgeChecksIds.includes(kc.id)).map(kc => kc.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.knowledgeChecks")}
                      name={nameof<typeof programRequest>("knowledgeChecksIds")}
                      value={programRequest.knowledgeChecksIds}
                      onChange={handleKnowledgeChecksChange}
                      selectData={codeBookToSelectData(knowledgeChecks)}
                      errorText={errors?.knowledgeChecksIds?.toString()}
                    />

                  }
                </Col>
                <Col md={4}>
                  {!canEditBasicCheck() &&
                    <>
                      <StaticField
                        label={t("request.enrollmentConditions")}
                        value={enrollmentConditions?.filter(ec => programRequest.enrollmentConditionsIds.includes(ec.id)).map(ec => ec.name).join(", ") ?? ""}
                        className={styles.changedField}
                      />
                    </>
                    ||
                    <Select
                      multiple
                      label={t("request.enrollmentConditions")}
                      name={nameof<typeof programRequest>("enrollmentConditionsIds")}
                      value={programRequest.enrollmentConditionsIds}
                      onChange={handleEnrollmentConditionsChange}
                      selectData={codeBookToSelectData(enrollmentConditions)}
                      errorText={errors?.enrollmentConditionsIds?.toString()}
                    />
                  }
                </Col>
              </Row>
              {programRequest.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
                ((!canEditBasicCheck() &&
                  <StaticField
                    label={t("request.otherFormsOfTeaching")}
                    value={programRequest.otherFormsOfTeaching.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherData
                      name={nameof<typeof programRequest>("otherFormsOfTeaching")}
                      header={t("request.formOfTeaching")}
                      label={t("request.otherFormsOfTeaching")}
                      addButtonLabel={t("request.addNewFormOfTeaching")}
                      values={programRequest.otherFormsOfTeaching}
                      onChange={handleChange(setProgramRequest)}
                      getErrorForPath={getErrorForPath}
                    />
                  ))
              }

              {programRequest.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
                ((!canEditBasicCheck() &&
                  <StaticField
                    label={t("request.otherKnowledgeChecks")}
                    value={programRequest.otherKnowledgeChecks.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherData
                      name={nameof<typeof programRequest>("otherKnowledgeChecks")}
                      header={t("request.knowledgeChecks")}
                      label={t("request.otherKnowledgeChecks")}
                      addButtonLabel={t("request.addNewKnowledgeCheck")}
                      values={programRequest.otherKnowledgeChecks}
                      onChange={handleChange(setProgramRequest)}
                      getErrorForPath={getErrorForPath}
                    />
                  ))
              }



              {programRequest.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
                ((!canEditBasicCheck() &&
                  <StaticField
                    label={t("request.otherEnrollmentConditions")}
                    value={programRequest.otherEnrollmentConditions.toString().replace(/,/g, ", ")}
                  />
                ) || (
                    <OtherEnrollmentConditionsInfo
                      name={`${nameof<typeof programRequest>("otherEnrollmentConditions")}`}
                      getErrorForPath={getErrorForPath}
                      otherEnrollmentConditions={programRequest.otherEnrollmentConditions}
                      onChange={handleChange(setProgramRequest)} />
                  ))
              }
              {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
                && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
                && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
                <>
                  {!canEditBasicCheck() &&
                    <>
                      {programRequest.competences && programRequest.competences.length > 0 &&
                        <>
                          <Row>
                            <Col md={12} className={styles.rowTitleSpacing}>
                              <Typography variant="smallHeading" component="h3">
                                {t("request.competences")}
                              </Typography>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              <Table {...tableProps} />
                            </Col>
                          </Row>
                        </>
                      }
                    </>
                    ||
                    <CompetenceInfo
                      name={`${nameof<typeof programRequest>("competences")}`}
                      getErrorForPath={getErrorForPath}
                      competences={programRequest.competences}
                      onChange={handleChange(setProgramRequest)}
                    />
                  }
                </>
              }

              {/* ovisno o statusu ide ili edit ili samo view */}
              {/* za prvu samo edit */}

              {(programRequest.statusId === ProgramRequestStatus.Evaluated || programRequest.statusId === ProgramRequestStatus.SentMZO ||
                programRequest.statusId === ProgramRequestStatus.SubmittedMZO || programRequest.statusId === ProgramRequestStatus.UnlockedMZO) &&
                <TeacherInfo
                    name={`${nameof<typeof programRequest>("teachers")}`}
                    teachers={programRequest.teachers}
                    onChange={handleChange(setProgramRequest)}
                    getErrorForPath={getErrorForPath}
                    doValidations={doValidations}
                  />
              }

              {(canEditBasicCheck() || (programRequest.additionalDocumentation && programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).length > 0)) &&
                <Row>
                  <Col md={12} className={styles.rowTitleSpacing}>
                    <Typography variant="smallHeading" component="h3">
                      {t("request.additionalDocumentationASOO")}
                    </Typography>
                  </Col>
                </Row>
              }              
              {programRequest.additionalDocumentation && programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).length > 0 &&
                <Row>
                  <Col md={12}>
                    {programRequest.additionalDocumentation && programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationASOO).map((document, index) => (
                      <DocumentListItem
                        key={index}
                        removable={canEditBasicCheck()}
                        document={document.additionalDocumentationDocument}
                        onRemoveClick={() => handleAdditionalDocumentationRemove(index)}
                        onDocumentClick={(doc) => handleAdditionalDocumentationDownload(doc)}
                      />
                    ))}
                  </Col>
                </Row>                
              }
              {canEditBasicCheck() &&
                <Row>
                  <Col md={4}>
                    <Button
                      outline
                      color={"primary"}
                      className={styles.uploadButton}
                      icon={<UploadIcon/>}
                      onClick={() => handleAddAdditionalDocumentationClick(AdditionalDocumentationType.AdditionalDocumentationASOO)}
                    >
                      {t("common.addDocument")}
                      {<span className={styles.tooltiptext}>{t("request.additionalDocumentationToolTipASOO")}</span>}
                    </Button>
                  </Col>
                </Row>
              }
              {(programRequest.statusId === ProgramRequestStatus.Evaluated || programRequest.statusId === ProgramRequestStatus.SentMZO ||
                programRequest.statusId === ProgramRequestStatus.SubmittedMZO || programRequest.statusId === ProgramRequestStatus.UnlockedMZO) &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.additionalDocumentationMZOM")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      {programRequest.additionalDocumentation 
                        && programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === AdditionalDocumentationType.AdditionalDocumentationMZOM).map((document, index) => (
                        <DocumentListItem
                          key={index}
                          document={document.additionalDocumentationDocument}
                          onRemoveClick={() => handleAdditionalDocumentationRemove(index)}
                          onDocumentClick={(doc) => handleAdditionalDocumentationDownload(doc)}
                        />
                      ))}
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Button
                        outline
                        color={"primary"}
                        className={styles.uploadButton}
                        icon={<UploadIcon />}
                        onClick={() => handleAddAdditionalDocumentationClick(AdditionalDocumentationType.AdditionalDocumentationMZOM)}
                      >
                        {t("common.addDocument")}
                        {<span className={styles.tooltiptext}>{t("request.additionalDocumentationToolTipMZOM")}</span>}
                      </Button>
                    </Col>
                  </Row>
                </>
              }

              {(programRequest.programTypeId === ProgramType.TrainingPrograms ||
                programRequest.programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
                programRequest.programTypeId === ProgramType.CraftsSpecializationPrograms ||
                programRequest.programTypeId === ProgramType.RefinementPrograms ||                
                programRequest.programTypeId === ProgramType.NewVocationalCurriculum
              ) &&

                <ProgramRequestModulePanel
                  name={`${nameof<typeof programRequest>("modules")}`}
                  modules={programRequest.modules}
                  programTypeId={programRequest.programTypeId}
                  qualificationTypeId={programRequest.qualificationTypeId ?? 0}
                  getErrorForPath={getErrorForPath}
                  onChange={handleChange(setProgramRequest)}
                  readonly={!canEditBasicCheck()}
                />
              }




              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("request.curriculum")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                {!canEditBasicCheck() &&
                  <Col md={12}>
                    <UploadButton
                      name={nameof<typeof programRequest>("curriculumProgramDocument")}
                      value={programRequest.curriculumProgramDocument}
                      documentType={DocumentType.CurriculumProgram}
                      disabled={true}
                    />
                  </Col>
                  ||
                  <Col md={12}>
                    <UploadButton
                      name={nameof<typeof programRequest>("curriculumProgramDocument")}
                      onFileAdded={handleCurriculumProgramAdded}
                      onFileRemoved={handleCurriculumProgramRemoved}
                      value={programRequest.curriculumProgramDocument}
                      documentType={DocumentType.CurriculumProgram}
                      toolTip={t("document.wordAndPdfTooltip")}
                      fileFilter={t("document.wordAndPdfFilter")}
                      errorText={errors?.curriculumProgramDocument}
                    />
                  </Col>
                }
              </Row>
              {canEditBasicCheck() &&
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.programExecutionPlace")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={4}>
                      <Checkbox
                        label={t("request.headquarter")}
                        name={nameof<typeof programRequest>("locationInsideHeadquarters")}
                        value={programRequest.locationInsideHeadquarters}
                        checked={programRequest.locationInsideHeadquarters}
                        errorText={getErrorForPath("locationChosen")}
                        onChange={handleChange(setProgramRequest)}
                      />
                    </Col>
                    <Col md={4}>
                      <Checkbox
                        label={t("request.outsideHeadquarter")}
                        name={nameof<typeof programRequest>("locationOutsideHeadquarters")}
                        value={programRequest.locationOutsideHeadquarters}
                        checked={programRequest.locationOutsideHeadquarters}
                        errorText={getErrorForPath("locationChosen")}
                        onChange={handleChange(setProgramRequest)}
                      />
                    </Col>
                  </Row>
                </>
              }
              {programRequest.locationInsideHeadquarters && institutionData !== undefined && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.headquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={3}>
                      <TextField
                        label={t("common.address")}
                        plaintext
                        value={institutionData.address}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.town")}
                        plaintext
                        value={institutionData.town.name}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.municipality")}
                        plaintext
                        value={institutionData.town.municipalityName}
                      />
                    </Col>
                    <Col md={3}>
                      <TextField
                        label={t("common.county")}
                        plaintext
                        value={institutionData.town.countyName}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {programRequest.locationOutsideHeadquarters && institutionData !== undefined && (
                <>
                  <Row>
                    <Col md={12} className={styles.rowTitleSpacing}>
                      <Typography variant="smallHeading" component="h3">
                        {t("request.outsideHeadquarterExecutionPlaceTitle")}
                      </Typography>
                    </Col>
                  </Row>
                  {programRequest.statusId !== ProgramRequestStatus.Evaluated &&
                    <Row className={styles.outsideHeadquarterSelectRow}>
                      <Col md={6}>
                        <Select
                          multiple
                          label=""
                          name={nameof<typeof programRequest>("locationOutsideHeadquartersIds")}
                          value={programRequest.locationOutsideHeadquartersIds}
                          onChange={handleChange(setProgramRequest)}
                          selectData={fillLocations() || []}
                          errorText={errors?.locationOutsideHeadquartersIds?.toString()}
                        />
                      </Col>
                    </Row>
                  }

                  {programRequest.locationOutsideHeadquartersIds !=
                    null &&
                    institutionData?.locations
                      .filter(x => programRequest.locationOutsideHeadquartersIds.includes(x.id))
                      .map(
                        (institutionLocation) => {
                          if (institutionLocation) {
                            return (
                              <Row>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.addressOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.address}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.municipalityOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.town.name}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.townOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.town.municipalityName}
                                  />
                                </Col>
                                <Col md={3}>
                                  <TextField
                                    label={t("request.countyOutsideHeadquarter")}
                                    plaintext
                                    value={institutionLocation.town.countyName}
                                  />
                                </Col>
                              </Row>
                            );
                          }
                          else {
                            return <></>
                          }
                        }
                      )}
                </>
              )}
              {programRequest.locationOtherLegalEntity && (
                <OtherLegalEntityInfos
                  name={`${nameof<typeof programRequest>("otherLegalEntityLocations")}`}
                  getErrorForPath={getErrorForPath}
                  otherLegalEntityLocations={programRequest.otherLegalEntityLocations}
                  onChange={handleChange(setProgramRequest)} />
              )}

              <Row>
                <Col md={12} className={styles.rowTitleSpacing}>
                  <Typography variant="smallHeading" component="h3">
                    {t("common.note")}
                  </Typography>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <TextArea
                    label={""}
                    name={nameof<typeof programRequest>("remarkInstitution")}
                    value={programRequest.remarkInstitution}
                    onChange={handleChange(setProgramRequest)}
                    ariaLabel={t("common.note")}
                    maxLength={700}
                    rows={4}
                    errorText={errors?.remarkInstitution}
                  />
                </Col>
              </Row>
            </ExpandablePanelBody>
          </ExpandablePanel>
        </Col>
      </Row>
      <Row style={{ marginLeft: "-15px", marginRight: "-15px" }} className={"pt-2"}>
        <Col md={3}>
          <Button
            outline
            onClick={cancelProgramRequest}
          >
            {t("common.cancel")}
          </Button>
        </Col>
        <Col md={9} className={styles.addDataButtonCol}>
          <Button
            onClick={() => submitProgramRequest(false)}
            disabled={!institutionData || !institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0}
            className={styles.saveButton}
          >
            {t("common.save")}
          </Button>
          <Button
            onClick={() => submitProgramRequest(true)}
            disabled={!institutionData || !institutionData.andragogicLeaders || institutionData.andragogicLeaders.length === 0}
          >
            {t("request.submitRequest")}
          </Button>
        </Col>
      </Row>
      <Modal
        type="confirm"
        open={openConfirmModal}
        cancelText={t("common.cancel")}
        confirmText={t("common.yesSaveChanges")}
        onClose={handleCancelConfirmModal}
        onConfirm={handleConfirmedSaveModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("request.areYouSureRequestSave")}
          </Typography>
          {t("request.dataConfirmationSubmission")}
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openConfirmAndSendModal}
        cancelText={t("common.cancel")}
        confirmText={t("request.confirmRequestSubmission")}
        onClose={handleCancelConfirmAndSendModal}
        onConfirm={handleConfirmedSaveAndSendModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("request.areYouSureRequestSubmission")}
          </Typography>
          {t("request.dataValidityConfirmationRequestSubmission")}
        </div>
      </Modal>
      <Modal
        type="message"
        open={openSuccessModal}
        confirmText={t("common.close")}
        onClose={handleConfirmedSuccessModal}
        onConfirm={handleConfirmedSuccessModal}
      >
        <div className={styles.modalMessageContentAlignCenter}>

          <Typography component="h1" variant="largeBold">
            {isSendRequest ? t("request.requestSuccessfullySubmittedAndSend") : t("request.requestSuccessfullySubmitted")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="confirm"
        open={openCancelModal}
        cancelText={t("common.noProperCase")}
        confirmText={t("common.yesCancel")}
        onClose={handleCancelCancelModal}
        onConfirm={handleConfirmedCancelModal}
      >
        <div>

          <Typography component="h1" variant="largeBold">
            {t("common.areYouSureCancelChanges")}
          </Typography>
          {t("common.areYouSureCancelChangesNote")}
        </div>
      </Modal>
      <DocumentsUploadModal        
        title={currentAdditionalDocumentationType === AdditionalDocumentationType.AdditionalDocumentationASOO ? t("request.additionalDocumentationASOO") : t("request.additionalDocumentationMZOM")}        
        infoPanelText={currentAdditionalDocumentationType === AdditionalDocumentationType.AdditionalDocumentationASOO ? t("request.additionalDocumentationPanelASOO") : t("request.additionalDocumentationPanelMZOM")}
        types={t("document.imagesAndPdfAndWordFilter")}
        allDocuments={programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === currentAdditionalDocumentationType).map(doc => doc.additionalDocumentationDocument)}
        onRemoveUploadedDocument={handleAdditionalDocumentationRemove}
        maxSize={10}        
        maxFiles={programRequest.additionalDocumentation ? 10 - programRequest.additionalDocumentation.filter(doc => doc.additionalDocumentationTypeId === currentAdditionalDocumentationType).length : 10}
        filesAction={undefined}
        open={openUploadAdditionalDocumentationModal}
        onConfirm={handleAdditionalDocumentationConfirm}
        onClose={() => setOpenUploadAdditionalDocumentationModal(false)}
        hideOldDocuments={false}
        btnCancelText={t("common.cancel")}
        btnOkText={t("common.addNew")}
      />
    </>
  );
}
